import { useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useTransfers } from "@/features/transfers/api";
import { TRANSFER_STATUSES } from "@/features/transfers/types";
import { TransferStatusBadge } from "@/features/transfers/badges";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked } from "@/lib/roles";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/transfers/")({
  component: TransfersIndex,
  validateSearch: (s: Record<string, unknown>) => ({ status: s.status as string | undefined }),
});

const REQUESTER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores", "dept_user"]);

function TransfersIndex() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { status: searchStatus } = Route.useSearch();
  const [status, setStatus] = useState<string>(searchStatus === "pending" ? "Pending Approval" : "all");
  const [search, setSearch] = useState("");
  const statusChoices = [...TRANSFER_STATUSES];
  const { data: rows = [], isLoading } = useTransfers({
    status: status === "all" ? null : status,
    search,
    branchId: effectiveBranchId,
  });

  const canCreate = (me?.roles ?? []).some((r) => REQUESTER_ROLES.has(r));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Asset Transfers</h1> <ReadOnlyPill module="transfers" />
          <p className="text-sm text-muted-foreground">Internal & external movements with dual-HOD approval.</p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate({ to: "/transfers/new" })}><Plus className="mr-1 h-4 w-4" />New Transfer</Button>
        )}
      </div>

      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-56" placeholder="Search transfer / asset…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No transfers found.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Transfer No</TableHead><TableHead>Asset</TableHead>
              <TableHead>From</TableHead><TableHead>To</TableHead>
              <TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/transfers/$transferId", params: { transferId: r.id } })}>
                  <TableCell className="font-mono text-xs">
                    <Link to="/transfers/$transferId" params={{ transferId: r.id }} className="text-primary hover:underline">{r.transfer_no}</Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-mono text-xs">{r.asset_code}</div>
                    <div className="text-xs text-muted-foreground">{r.asset_name}</div>
                  </TableCell>
                  <TableCell className="text-xs">{[r.from_branch_name, r.from_dept_name].filter(Boolean).join(" / ") || "—"}</TableCell>
                  <TableCell className="text-xs">{[r.to_branch_name, r.to_dept_name].filter(Boolean).join(" / ") || "—"}</TableCell>
                  <TableCell><Badge variant={r.is_external ? "destructive" : "secondary"}>{r.is_external ? "External" : "Internal"}</Badge></TableCell>
                  <TableCell><TransferStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
